import { inject, Injectable } from '@angular/core';
import { DayJSDateFormat } from '@app/core/enums/date-format.enum';
import { DayjsLocaleService } from '@app/core/i18n/services/dayjs-locale.service';
import { LocaleNumberPipe } from '@app/shared/pipes/locale-number/locale-number.pipe';
import { Length } from '@app/shared/units/length/length.model';
import { LengthUnit, MetricLengthUnit } from '@app/shared/units/length/length-unit.enum';
import { _, TranslateService } from '@ngx-translate/core';
import { IPosition } from '@app/core/interfaces/position.interface';
import { ICustomCoordinates } from '@app/api/interfaces/common/custom-coordinates';
import { CoordinateSystemsLookupService } from '@app/core/coordinate-systems/services/coordinate-systems-lookup.service';

export type CsvCellValue = string | number | null | undefined;

@Injectable({
  providedIn: 'root',
})
export class CsvExportFormattingService {
  private readonly localeNumber = inject(LocaleNumberPipe);
  private readonly dayjsLocale = inject(DayjsLocaleService);
  private readonly translate = inject(TranslateService);
  private readonly coordinateSystems = inject(CoordinateSystemsLookupService);

  private readonly EMPTY_CELL = '';

  private readonly NO_ROUNDING_FRACTION_DIGITS = 20;

  public formatNumber(value: number | null | undefined, minDecimals = 0, maxDecimals?: number): string {
    if (value === null || value === undefined) {
      return this.EMPTY_CELL;
    }

    return this.localeNumber.transform(value, minDecimals, maxDecimals ?? this.NO_ROUNDING_FRACTION_DIGITS, {
      useGrouping: false,
    });
  }

  public formatDate(
    date: string | Date | null | undefined,
    format: DayJSDateFormat = DayJSDateFormat.YYYYMMDD
  ): string {
    return date ? this.dayjsLocale.format(date, format) : this.EMPTY_CELL;
  }

  public formatDateTime(
    date: string | Date | null | undefined,
    format: DayJSDateFormat = DayJSDateFormat.YYYYMMDD_HHmm
  ): string {
    return this.formatDate(date, format);
  }

  public formatPercent(value: number | null | undefined, minDecimals = 0, maxDecimals = 2): string {
    if (value === null || value === undefined) {
      return this.EMPTY_CELL;
    }

    return `${this.formatNumber(value, minDecimals, maxDecimals)}%`;
  }

  public formatLength(
    value: number | null | undefined,
    targetUnit: LengthUnit,
    decimals = 2,
    sourceUnit: LengthUnit = MetricLengthUnit.Meter
  ): string {
    if (value === null || value === undefined) {
      return this.EMPTY_CELL;
    }

    const converted = new Length(value, sourceUnit).round(targetUnit, decimals);

    return this.formatNumber(converted, 0, decimals);
  }

  public formatCoordinates(value: IPosition | null | undefined): string {
    if (value === null || value === undefined) {
      return this.EMPTY_CELL;
    }

    return this.translate.instant(_('@@shared-value__coordinates'), {
      I18N_VAR_LATITUDE: this.formatNumber(value.lat, 0, 6),
      I18N_VAR_LONGITUDE: this.formatNumber(value.lon, 0, 6),
    });
  }

  public formatCustomCoordinates(value: ICustomCoordinates | null | undefined, divider = ' / '): string {
    const system = this.coordinateSystems.getFor(value);

    if (!value || !system) {
      return this.EMPTY_CELL;
    }

    return [
      this.formatCoordinateAxis(system.axis1Label, value.axis1, system.axis1Unit),
      this.formatCoordinateAxis(system.axis2Label, value.axis2, system.axis2Unit),
    ].join(divider);
  }

  private formatCoordinateAxis(label: string, value: number, unit: string): string {
    return this.translate.instant(
      unit ? _('@@shared-value__custom_coordinate_axis') : _('@@shared-value__custom_coordinate_axis_no_unit'),
      {
        I18N_VAR_LABEL: label,
        I18N_VAR_VALUE: this.formatNumber(value, 0, 2),
        I18N_VAR_UNIT: unit,
      }
    );
  }

  public formatString(value: CsvCellValue): string {
    return value === null || value === undefined ? this.EMPTY_CELL : String(value);
  }

  /**
   * Formats a value of unknown type by dispatching on its runtime type: numbers are locale-formatted,
   * strings are passed through, anything else (null / undefined / object / boolean) yields `noDataValue`.
   * Use it when the value type is not known upfront (e.g. a generic table field). When `noDataValue`
   * is omitted the service default (an empty cell) is used.
   */
  public formatValue(value: unknown, noDataValue: string = this.EMPTY_CELL): string {
    if (typeof value === 'number') {
      return this.formatNumber(value);
    }

    if (typeof value === 'string') {
      return value;
    }

    return noDataValue;
  }
}
